import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc, query, orderBy
} from 'firebase/firestore';
import {
  Box, Typography, Paper, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Alert, CircularProgress, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';

// Roles assignable in the form (admin can grant any of these)
const ROLE_OPTIONS = ['admin', 'coach', 'assistant_coach', 'tournament_admin', 'volunteer', 'court_admin', 'member_manager'];

// Staff roles that determine whether a user appears on this page.
// Players/parents/etc. are NOT staff and don't belong here.
const STAFF_ROLES = ['admin', 'coach', 'assistant_coach', 'tournament_admin', 'volunteer', 'court_admin', 'member_manager'];

const ROLE_COLORS = {
  admin:            'error',
  coach:            'primary',
  assistant_coach:  'info',
  tournament_admin: 'warning',
  volunteer:        'success',
  player:           'secondary',
  court_admin:      'info',
  member_manager:   'default',
};

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  notes: '',
  roles: [],
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null); // null = new user
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'users'), orderBy('lastName')));
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Only show staff accounts (admin, coach, etc.)
      const staff = all.filter(u =>
        u.roles && u.roles.some(r => STAFF_ROLES.includes(r))
      );
      setUsers(staff);
    } catch (e) {
      logError('AdminUsers', e.message || String(e), e.stack || '');
      setError('Failed to load users.');
    }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const openNew = () => {
    setEditUser(null);
    setForm(emptyForm);
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({
      firstName: u.firstName || '',
      lastName:  u.lastName  || '',
      email:     u.email     || '',
      phone:     u.phone     || '',
      notes:     u.notes     || '',
      roles:     u.roles     || [],
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setError('');
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError('First name, last name, and email are required.');
      return;
    }
    if (form.roles.length === 0) {
      setError('At least one role is required.');
      return;
    }

    // Admin safeguards on edit
    if (editUser) {
      const wasAdmin = (editUser.roles || []).includes('admin');
      const willBeAdmin = form.roles.includes('admin');
      const currentUid = auth.currentUser?.uid;

      // Safeguard 1: don't let an admin remove their own admin role
      if (wasAdmin && !willBeAdmin && editUser.id === currentUid) {
        setError("You can't remove your own admin role.");
        return;
      }

      // Safeguard 2: don't allow removal of admin from the last admin
      if (wasAdmin && !willBeAdmin) {
        const otherAdmins = users.filter(x =>
          x.id !== editUser.id && (x.roles || []).includes('admin')
        );
        if (otherAdmins.length === 0) {
          setError("Can't remove admin role — this is the last admin. Promote another user first.");
          return;
        }
      }
    }

    setSaving(true);
    try {
      if (editUser) {
        // Update existing doc
        console.log('Saving roles:', form.roles, 'for user:', editUser.id);
        await updateDoc(doc(db, 'users', editUser.id), {
          firstName: form.firstName.trim(),
          lastName:  form.lastName.trim(),
          email:     form.email.trim().toLowerCase(),
          phone:     form.phone.trim(),
          notes:     form.notes.trim(),
          roles:     form.roles,
        });
        setSuccess(`${form.firstName} ${form.lastName} updated.`);
      } else {
        // New staff user — create a placeholder doc.
        // The UID will be updated when they first sign in via Google/Apple.
        // We use email as the temp doc ID so we can locate it on first login.
        const tempId = `pending_${form.email.trim().toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
        await setDoc(doc(db, 'users', tempId), {
          uid:                  tempId,
          firstName:            form.firstName.trim(),
          lastName:             form.lastName.trim(),
          email:                form.email.trim().toLowerCase(),
          phone:                form.phone.trim(),
          notes:                form.notes.trim(),
          roles:                form.roles,
          registrationComplete: true,
          notifyByEmail:        true,
          notifyBySMS:          false,
          linkedAthletes:       [],
          pendingLinkEmail:     true,
        });
        setInviteData({ id: tempId, firstName: form.firstName, email: form.email, phone: form.phone, notes: form.notes });
        setInviteDialog(true);
        setInviteSent(false);
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (e) {
      logError('AdminUsers', e.message || String(e), e.stack || '');
      setError('Save failed: ' + e.message);
    }
    setSaving(false);
  };

  const handleDelete = async (u) => {
    const currentUid = auth.currentUser?.uid;
    const targetRoles = u.roles || [];
    const targetIsAdmin = targetRoles.includes('admin');

    // Safeguard 1: prevent deleting your own account
    if (u.id === currentUid) {
      setError("You can't remove your own account.");
      return;
    }

    // Safeguard 2: prevent deleting the last admin
    if (targetIsAdmin) {
      const otherAdmins = users.filter(x =>
        x.id !== u.id && (x.roles || []).includes('admin')
      );
      if (otherAdmins.length === 0) {
        setError(`Can't remove ${u.firstName} ${u.lastName} — they're the only admin. Promote another user first.`);
        return;
      }
    }

    try {
      await deleteDoc(doc(db, 'users', u.id));
      setSuccess(`${u.firstName} ${u.lastName} removed.`);
      fetchUsers();
    } catch (e) {
      logError('AdminUsers', e.message || String(e), e.stack || '');
      setError('Delete failed: ' + e.message);
    }
    setDeleteConfirm(null);
  };

  const handleSendInvite = async () => {
    setInviteSending(true);
    try {
      const functions = getFunctions();
      const sendInvite = httpsCallable(functions, 'sendCoachInvite');
      await sendInvite({ coachId: inviteData.id, coachEmail: inviteData.email, coachFirstName: inviteData.firstName, coachPhone: inviteData.phone, coachNotes: inviteData.notes });
      setInviteSent(true);
    } catch (err) {
      console.error('Invite failed:', err);
      setInviteSent(true);
    }
    setInviteSending(false);
  };

  return (
    <Layout>
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AdminPanelSettingsIcon sx={{ color: '#1E3A5F', fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E3A5F' }}>
            Manage Admin Users
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
          sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}>
          Add User
        </Button>
      </Box>

      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
      {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#1E3A5F' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Roles</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: '#888' }}>
                    No staff accounts found.
                  </TableCell>
                </TableRow>
              )}
              {users.map(u => (
                <TableRow key={u.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{u.firstName} {u.lastName}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(u.roles || []).map(r => (
                        <Chip key={r} label={r.replace('_', ' ')} size="small"
                          color={ROLE_COLORS[r] || 'default'} />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {u.pendingLinkEmail
                      ? <Chip label="Pending" size="small" color="warning" />
                      : <Chip label="Active"  size="small" color="success" />
                    }
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(u)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirm(u)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
          {editUser ? 'Edit Staff Account' : 'Add Staff Account'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
              label="First Name" fullWidth
              value={form.firstName}
              onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
            />
            <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
              label="Last Name" fullWidth
              value={form.lastName}
              onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
            />
          </Box>
          <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1, mb: 2 }}
            label="Email" fullWidth type="email"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            disabled={!!editUser}
            helperText={editUser ? 'Email cannot be changed.' : 'Must match their Google or Apple sign-in email.'}
          />
          <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1, mb: 2 }}
            label="Phone" fullWidth
            value={form.phone}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            placeholder="+14075551234"
          />
          <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1, mb: 2 }}
            label="Notes (optional)" fullWidth multiline rows={2}
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="e.g. Pay rate, schedule notes..."
          />
          <Box>
            <Typography variant="body2" sx={{ color: '#555', mb: 1 }}>Roles — click to toggle</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {ROLE_OPTIONS.map(role => {
                const selected = form.roles.includes(role);
                return (
                  <Chip
                    key={role}
                    label={role.replace('_', ' ')}
                    onClick={() => setForm(f => ({
                      ...f,
                      roles: selected ? f.roles.filter(r => r !== role) : [...f.roles, role]
                    }))}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={{
                      textTransform: 'capitalize',
                      cursor: 'pointer',
                      backgroundColor: selected ? '#1E3A5F' : 'transparent',
                      color: selected ? 'white' : '#1E3A5F',
                      borderColor: '#1E3A5F',
                      '&:hover': { backgroundColor: selected ? '#2E6DA4' : '#DEEAF1' },
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {!editUser && (
            <Alert severity="info" sx={{ mt: 2 }}>
              The new user will appear as <strong>Pending</strong> until they sign in with Google or Apple
              using this email address. Their account activates automatically on first login.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button type="button" variant="contained" onClick={handleSave} disabled={saving}
            sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}>
            {saving ? <CircularProgress size={20} /> : editUser ? 'Save Changes' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Remove Staff Account</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{deleteConfirm?.firstName} {deleteConfirm?.lastName}</strong> ({deleteConfirm?.email})?
            They will lose access to the app immediately.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm)}>Remove</Button>
        </DialogActions>
      </Dialog>
      {/* Staff Invite Dialog */}
      <Dialog open={inviteDialog} onClose={() => setInviteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>User Created</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>{inviteData?.firstName}</strong> has been added.
          </Typography>
          <Typography variant="body2" sx={{ color: '#555' }}>
            Would you like to send an invite email to <strong>{inviteData?.email}</strong> so they can complete their account setup?
          </Typography>
          {inviteData?.phone && (
            <Typography variant="body2" sx={{ color: '#555', mt: 1 }}>📞 {inviteData.phone}</Typography>
          )}
          {inviteData?.notes && (
            <Typography variant="body2" sx={{ color: '#555', mt: 0.5, fontStyle: 'italic' }}>📝 {inviteData.notes}</Typography>
          )}
          {inviteSent && <Alert severity="success" sx={{ mt: 2 }}>Invite sent successfully!</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setInviteDialog(false)} sx={{ color: '#888' }}>Skip</Button>
          <Button variant="contained" onClick={handleSendInvite}
            disabled={inviteSending || inviteSent}
            sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}>
            {inviteSending ? 'Sending...' : inviteSent ? 'Sent!' : 'Send Invite Email'}
          </Button>
          {inviteSent && (
            <Button variant="outlined" onClick={() => setInviteDialog(false)}
              sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
    </Layout>
  );
}
